/** @prettier */

import {useEffect, useState} from 'react';
import {Box, ToggleButtonGroup, ToggleButton, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';

//Style import
import '../css/common/common.css';

import {initialUpper} from '../functions/convertString';
import {iconBlack, iconWhite} from '../define';

/**
 * 指令画面運転モードボタン群
 * @param props props
 * - displayMode 選択された運転モード名称
 * - setOpeMode　運転モード選択設定関数
 * @returns JSX
 */
const RunModeButtonGroup = (props: {
  displayMode:
    | 'dehumidify'
    | 'auto'
    | 'cooling'
    | 'heating'
    | 'fan'
    | undefined;
  setOpeMode: React.Dispatch<
    React.SetStateAction<
      'auto' | 'dehumidify' | 'cooling' | 'fan' | 'heating' | undefined
    >
  >;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const {displayMode, setOpeMode} = props;

  const [mode, setMode] = useState<string | undefined>(props.displayMode); //運転モード

  //運転モードの更新
  useEffect(() => {
    if (displayMode === 'dehumidify') {
      setMode(initialUpper('Dry'));
    } else if (displayMode !== undefined) {
      setMode(initialUpper(displayMode));
    }
    setOpeMode(displayMode);
  }, [displayMode, setOpeMode]);

  /**
   * 運転モードボタン押下時処理
   * @param {React.MouseEvent<HTMLElement>} event マウスイベント
   * @param {string | null} newAlignment ボタンが押された運転モード
   */
  const handleAlignment = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    if (newAlignment !== null) {
      setMode(initialUpper(newAlignment));
      // 運転モード「Dry」のみ画面表示文字列と内部で保持する値が異なっているため変換
      if (newAlignment === 'Dry') newAlignment = 'dehumidify';

      const temp = newAlignment.toLowerCase();
      if (
        temp === 'dehumidify' ||
        temp === 'auto' ||
        temp === 'cooling' ||
        temp === 'heating' ||
        temp === 'fan'
      ) {
        props.setOpeMode(temp);
      }
    }
  };

  return (
    <Stack spacing={2}>
      <Box
        id="mv-txtOperationMode"
        className="operation__card__content__setting__value__currentValue">
        {trans(`RunModeButtonGroup.mode.${mode?.toLowerCase()}`, {
          defaultValue: '', //modeが'Cooling' or 'Heating' or 'Fan' or 'Dry' or 'Auto'以外の場合
        })}
      </Box>
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={handleAlignment}
        aria-label="Operation mode change"
        className="operation__card__content__setting__value__toggleButton">
        <ToggleButton
          title={`${trans('RunModeButtonGroup.image.title.auto')}`}
          value="Auto"
          aria-label="auto"
          className={
            mode === 'Auto'
              ? 'operation__card__content__setting__value__toggleButton--mode--selectedAuto'
              : ''
          }>
          <img
            id="mv-btnSelectOperationModeAuto"
            className="operation__card__content__setting__value__toggleButton__image"
            src={mode === 'Auto' ? iconWhite.AUTO.src : iconBlack.AUTO.src}
            alt={
              mode === 'Auto'
                ? `${trans(
                    `RunModeButtonGroup.image.alternativeText.${iconWhite.AUTO.alt}`
                  )}`
                : `${trans(
                    `RunModeButtonGroup.image.alternativeText.${iconBlack.AUTO.alt}`
                  )}`
            }
          />
        </ToggleButton>
        <ToggleButton
          title={`${trans('RunModeButtonGroup.image.title.cooling')}`}
          value="Cooling"
          aria-label="cooling"
          className={
            mode === 'Cooling'
              ? 'operation__card__content__setting__value__toggleButton--mode--selectedCooling'
              : ''
          }>
          <img
            id="mv-btnSelectOperationModeCooling"
            className="operation__card__content__setting__value__toggleButton__image"
            src={
              mode === 'Cooling' ? iconWhite.COOLING.src : iconBlack.COOLING.src
            }
            alt={
              mode === 'Cooling'
                ? `${trans(
                    `RunModeButtonGroup.image.alternativeText.${iconWhite.COOLING.alt}`
                  )}`
                : `${trans(
                    `RunModeButtonGroup.image.alternativeText.${iconBlack.COOLING.alt}`
                  )}`
            }
          />
        </ToggleButton>
        <ToggleButton
          title={`${trans('RunModeButtonGroup.image.title.heating')}`}
          value="Heating"
          aria-label="heating"
          className={
            mode === 'Heating'
              ? 'operation__card__content__setting__value__toggleButton--mode--selectedHeating'
              : ''
          }>
          <img
            id="mv-btnSelectOperationModeHeating"
            className="operation__card__content__setting__value__toggleButton__image"
            src={
              mode === 'Heating' ? iconWhite.HEATING.src : iconBlack.HEATING.src
            }
            alt={
              mode === 'Heating'
                ? `${trans(
                    `RunModeButtonGroup.image.alternativeText.${iconWhite.HEATING.alt}`
                  )}`
                : `${trans(
                    `RunModeButtonGroup.image.alternativeText.${iconBlack.HEATING.alt}`
                  )}`
            }
          />
        </ToggleButton>
        <ToggleButton
          title={`${trans('RunModeButtonGroup.image.title.fan')}`}
          value="Fan"
          aria-label="fan"
          className={
            mode === 'Fan'
              ? 'operation__card__content__setting__value__toggleButton--mode--selectedFan'
              : ''
          }>
          <img
            id="mv-btnSelectOperationModeFan"
            className="operation__card__content__setting__value__toggleButton__image"
            src={mode === 'Fan' ? iconWhite.BLOWER.src : iconBlack.BLOWER.src}
            alt={
              mode === 'Fan'
                ? `${trans(
                    `RunModeButtonGroup.image.alternativeText.${iconWhite.BLOWER.alt}`
                  )}`
                : `${trans(
                    `RunModeButtonGroup.image.alternativeText.${iconBlack.BLOWER.alt}`
                  )}`
            }
          />
        </ToggleButton>
        <ToggleButton
          title={`${trans('RunModeButtonGroup.image.title.dry')}`}
          value="Dry"
          aria-label="dehumidify"
          className={
            mode === 'Dry'
              ? 'operation__card__content__setting__value__toggleButton--mode--selectedDry'
              : ''
          }>
          <img
            id="mv-btnSelectOperationModeDry"
            className="operation__card__content__setting__value__toggleButton__image"
            src={mode === 'Dry' ? iconWhite.DRYING.src : iconBlack.DRYING.src}
            alt={
              mode === 'Dry'
                ? `${trans(
                    `RunModeButtonGroup.image.alternativeText.${iconWhite.DRYING.alt}`
                  )}`
                : `${trans(
                    `RunModeButtonGroup.image.alternativeText.${iconBlack.DRYING.alt}`
                  )}`
            }
          />
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
};

export default RunModeButtonGroup;
